import { CustomerService, IApplicationFilter, property, widget, WidgetPropertyType } from '@dynamik/common';
import { InstrumentWithValuesViewModel, OverviewClient, OverviewRequest } from '@dynamik/common/clients/reporting-clients';
import { OverviewClient as TransactionOverviewClient } from '@dynamik/common/clients/transaction-clients';
import { BindingEngine ,computedFrom} from 'aurelia-binding';
import { WidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';
import { CategoriesClient } from '@dynamik/common/clients/transaction-clients';
import { CategoryType } from '../../../classes/category-type';

declare module '@dynamik/common/clients/reporting-clients' {
  interface InstrumentWithValuesViewModel {
    latestAccountNumber?: number;
    groupTotal?: boolean;
  }
}

@customElement('value-adjustment-widget')
@widget('Value adjustment', true, false, 'Description')
export class ValueAdjustmentWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, bindingEngine: BindingEngine, private categoriesClient: CategoriesClient,
    private overviewClient: OverviewClient, private transactionOverviewClient: TransactionOverviewClient) {
    super(element, bindingEngine, customerService);
  }

  allInstrumentValues: InstrumentWithValuesViewModel[];
  instrumentValues: InstrumentWithValuesViewModel[];
  total: InstrumentWithValuesViewModel;
  suppressSaveColumnState: boolean;
  currentPage = 0;
  lastItemRendered: (value: unknown) => void;

  @property('Data', 'Portfolio', WidgetPropertyType.List)
  portfolio?: string;
  async portfolioListValues() {
    return [
      { value: undefined, label: 'All portfolios' },
      ...(await this.categoriesClient.getSecCategories(this.customerService.selectedCustomer.companyId, CategoryType.Portfolio))
        .map(x => ({ value: x.name, label: x.name }))
    ];
  }
  portfolioChanged(newValue: string, oldValue: string) {
    if (!this.title) {
      return;
    }

    this.title = this.title.replace(` (${oldValue})`, '') + (newValue ? ` (${newValue})` : '');
  }

  @property('Data')
  quantityPrecision?: number = 0;

  @property('Data')
  precision: number = 2;

  @property('Table')
  font?: string;

  @property('Table')
  fontSize?: number = 10;

  @property('Table')
  color?: string;

  @property('Table')
  rowHeight?: number;

  @property('Table')
  rowsPerPage: number = 30;

  @property('Table')
  columnWidths?: string;

  @property()
  captionFont?: string;

  @property()
  captionFontSize?: number = 10;

  @property()
  captionColor?: string;
  @computedFrom('customerService.selectedCustomer.useAverageCostPrice')
  get displayAvg() {
    return this.customerService.selectedCustomer.useAverageCostPrice;
  }
  @computedFrom('customerService.selectedCustomer.useAverageCostPrice')
  get displayFifo() {
    return !this.customerService.selectedCustomer.useAverageCostPrice;
  }

  protected override async loadData(filter: IApplicationFilter) {
    const [instruments, latestAccounts] = await Promise.all([
      this.overviewClient.get(filter.customer.companyId, new OverviewRequest({ showAll: false, toDate: filter.dates.toStr, portfolio: this.portfolio })),
      this.transactionOverviewClient.latestInstrumentAccounts(filter.customer.companyId, filter.dates.toStr)
    ]);
    instruments.rows.forEach(x => {
      x.latestAccountNumber = latestAccounts.find(y => y.instrumentUuid === x.uuid)?.latestAccountNumber;
    });
    return instruments.rows;
  }

  protected override async setData(data: InstrumentWithValuesViewModel[]) {
    this.total = this.getTotal(data);
    const grouped = Array.from(data.groupBy(x => x.latestAccountNumber)).sort((a, b) => (a[0] ?? 99999) - (b[0] ?? 99999));
    data = [];
    for (const [key, value] of grouped) {
      data.push(...value.sort((a, b) => a.name.localeCompare(b.name)));
      const groupTotal = this.getTotal(value);
      groupTotal.groupTotal = true;
      data.push(groupTotal);
      // add empty row
      data.push(new InstrumentWithValuesViewModel());
    }

     this.allInstrumentValues = data;
     this.allInstrumentValues.push(this.total);
     this.excelData = {
       fileName: 'Value Adjustment',
       data: { Items: this.allInstrumentValues }
     };
     this.updatePageData();
  }

  private updatePageData() {
    this.instrumentValues = this.allInstrumentValues.filter((x, i) => this.currentPage * this.rowsPerPage <= i && i < (this.currentPage + 1) * this.rowsPerPage);
  }

  getTotal(x: InstrumentWithValuesViewModel[]) {
    return x.reduce((p, c) => {
          p.totalCostPrice += c.totalCostPrice;
          p.avgCostPrice += c.avgCostPrice ?? 0;
          p.estimatedValue += c.estimatedValue;
          p.accountingValue += c.accountingValue;
          p.avgAccountingValue += c.avgAccountingValue ?? 0;
          p.unrealizedAccountingValue += c.unrealizedAccountingValue;
          p.avgUnrealizedAccountingValue += c.avgUnrealizedAccountingValue ?? 0;
      return p;
    },
        new InstrumentWithValuesViewModel({
          name: '',
          totalCostPrice: 0,
          avgCostPrice: 0,
          estimatedValue: 0,
          accountingValue: 0,
          avgAccountingValue: 0,
          unrealizedAccountingValue: 0,
          avgUnrealizedAccountingValue: 0
        }));
  }

  getPageCount(): number {
    return this.instrumentValues ? Math.ceil(this.allInstrumentValues.length / this.rowsPerPage) : 0;
  }

  async setPage(page: number): Promise<void> {
    if (page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.updatePageData();
    await new Promise(resolve => this.lastItemRendered = resolve);
  }

  override configChanged(key: keyof ValueAdjustmentWidget, newValue: any, oldValue: any) {
    switch (key) {
      case 'rowsPerPage':
        this.updatePageData();
        break
      case 'portfolio':
        this.portfolioChanged(newValue, oldValue);
      case 'quantityPrecision':
      case 'precision':
        this.refresh$.next();
        break;
      default:
        break;
    }
  }
}
